import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';

import Auth from '../../authorization/Auth';
import { DEFAULT_LOCALE, scsKeys } from '../../constants';
import {
  setAllowPromoteApp, setCountry, setIsMarkdownEnabled, setLocale, setIsCreatePromotionEnabled, setIsCsvUploadEnabled
} from '../../redux/store';
import { getStore, getPromoteConfig } from '../../service-calls';
import NativeBridge from '../../utils/native-bridge';
import { isAllTranslationsPresent } from '../../utils/translationsUtils';

import LoginForm from './LoginForm';

const LogIn = () => {
  const [auth, setAuth] = useState(false);
  const [storeCurrency, setStoreCurrency] = useState('');
  const [storeId, setStoreId] = useState('');
  const [storeLocale, setStoreLocale] = useState('');
  const [storeTimezone, setStoreTimezone] = useState('');
  const [error, setError] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [athleteNumber, setAthleteNumber] = useState('');
  const [athletePassword, setAthletePassword] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [isNativeLoading, setIsNativeLoading] = useState(NativeBridge.isNativeApp());
  const dispatch = useDispatch();

  // --- Shared helpers used by both native and web login flows ------------

  const saveStoreToLocalStorage = ({
    country, currency, id, locale, storeNumber, region, timezone
  }) => {
    localStorage.setItem('country', country);
    localStorage.setItem('storeCurrency', currency);
    localStorage.setItem('storeId', id);
    localStorage.setItem('storeLocale', locale);
    localStorage.setItem('storeNumber', storeNumber);
    localStorage.setItem('storeRegion', region);
    localStorage.setItem('storeTimezone', timezone);
  };

  const saveScsConfig = (data) => {
    Object.keys(data).forEach((key) => {
      localStorage.setItem(`scs.${key}`, data[key]);
    });
  };

  const updateStoreState = ({
    country, storeNumber, currency, id, locale, timezone
  }) => {
    setSelectedCountry(country);
    setSelectedStore(storeNumber);
    setStoreCurrency(currency);
    setStoreId(id);
    setStoreLocale(locale);
    dispatch(setLocale(locale));
    setStoreTimezone(timezone);
  };

  const saveTranslationsFlag = (locale) => {
    const isTranslationsAvailable = locale === DEFAULT_LOCALE
      || isAllTranslationsPresent(locale);
    localStorage.setItem('isTranslationsAvailable', isTranslationsAvailable);
  };

  const dispatchFeatureFlags = () => {
    dispatch(setIsMarkdownEnabled(localStorage.getItem(scsKeys.createMarkdowns) === 'true'));
    dispatch(setIsCreatePromotionEnabled(localStorage.getItem(scsKeys.createPromotions) === 'true'));
    dispatch(setIsCsvUploadEnabled(localStorage.getItem(scsKeys.isStyleUploadEnabled) === 'true'));
    dispatch(setAllowPromoteApp(localStorage.getItem(scsKeys.allowPromoteApp) === 'true'));
  };

  const loadPromoteConfig = async (country, storeNumber) => {
    const { data, errorMessage } = await getPromoteConfig(country, storeNumber);
    if (errorMessage) {
      localStorage.setItem(scsKeys.allowPromoteApp, 'false');
      throw new Error(errorMessage);
    }
    saveScsConfig(data);
  };

  // --- Effects -----------------------------------------------------------

  useEffect(() => {
    dispatch(setCountry(selectedCountry));
    return () => {
      dispatch(setCountry(selectedCountry));
    };
  }, [selectedCountry, dispatch]);

  // --- Native bridge auto-login ------------------------------------------
  useEffect(() => {
    if (!NativeBridge.isNativeApp()) return;

    const nativeLogin = async () => {
      setIsNativeLoading(true);
      setError('');

      try {
        const token = await NativeBridge.requestAthleteToken();
        const payload = Auth.loginWithNativeToken(token);

        const country = payload.nike_country;
        const storeNumber = payload.nike_store;
        const storeData = await getStore(country, storeNumber);

        saveStoreToLocalStorage({
          country,
          currency: storeData.currency,
          id: storeData.id,
          locale: storeData.locale,
          region: storeData.region,
          storeNumber,
          timezone: storeData.timezone,
        });

        await loadPromoteConfig(country, storeNumber);

        updateStoreState({
          country,
          currency: storeData.currency,
          id: storeData.id,
          locale: storeData.locale,
          storeNumber,
          timezone: storeData.timezone,
        });

        saveTranslationsFlag(storeData.locale);
        dispatchFeatureFlags();
        setAuth(true);
      } catch (err) {
        setError(err.message || 'Native authentication failed');
        setIsNativeLoading(false);
      }
    };

    nativeLogin();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Web username/password helpers -------------------------------------

  const formIsNotFilled = () => !(
    athleteNumber.length
    && athletePassword.length
    && selectedCountry?.length
    && selectedStore.length
  );

  const onSubmit = async () => {
    setIsFetching(true);
    setError('');

    try {
      await Auth.login({
        athleteNumber,
        athletePassword,
        selectedCountry,
        selectedStore,
      });
      const {
        currency, id, locale, region, timezone
      } = await getStore(selectedCountry, selectedStore);

      saveStoreToLocalStorage({
        country: selectedCountry,
        currency,
        id,
        locale,
        region,
        storeNumber: selectedStore,
        timezone,
      });

      await loadPromoteConfig(selectedCountry, selectedStore);

      updateStoreState({
        country: selectedCountry,
        currency,
        id,
        locale,
        storeNumber: selectedStore,
        timezone,
      });

      saveTranslationsFlag(locale);
      dispatchFeatureFlags();
      setAuth(true);
    } catch (err) {
      setError(err.message || 'Sign in Failed');
      localStorage.clear();
      setAuth(false);
    } finally {
      setIsFetching(false);
    }
  };

  // --- Render ------------------------------------------------------------

  if (auth) {
    return (
      <Redirect
        to={{
          pathname: '/promotions',
          state: {
            country: selectedCountry,
            storeCurrency,
            storeId,
            storeLocale,
            storeNumber: selectedStore,
            storeTimezone,
          },
        }}
      />
    );
  }

  if (isNativeLoading) {
    return (
      <section className="ncss-col-sm-12 ta-sm-c mt10-sm">
        <h1 className="display-3 mb4-sm">Signing in…</h1>
        <p className="body-2 text-color-secondary">Authenticating with Nike app</p>
      </section>
    );
  }

  if (NativeBridge.isNativeApp() && error) {
    return (
      <section className="ncss-col-sm-12 ta-sm-c mt10-sm">
        <h1 className="display-3 mb4-sm">Promote App</h1>
        <p className="body-2 text-color-error">{error}</p>
      </section>
    );
  }

  return (
    <LoginForm
      disableSubmit={formIsNotFilled()}
      errorMessage={error}
      fetching={isFetching}
      loginInformation={{
        athleteNumber, athletePassword, selectedCountry, selectedStore
      }}
      setAthleteNumber={setAthleteNumber}
      setAthletePassword={setAthletePassword}
      setSelectedCountry={setSelectedCountry}
      setSelectedStore={setSelectedStore}
      onSubmit={onSubmit}
    />
  );
};

export default LogIn;
